import { useCallback, useEffect, useState } from 'react'
import { Chip } from '../components/interviews/Chip'
import { useDisputeFine, useGetMyFines } from '../hooks/backend/driver'
import { CONSENT_LABEL, OUTCOME_LABEL, rand, type ConsentStatus, type DisputeOutcome } from '../utils/drivingFines'
import { readableDate } from '../utils/coachingDates'

/**
 * A driver's own speeding fines on /board (clause 21.6A, db/migrations/0484).
 *
 * Only fines he has been given the notice for, only his. While the window is
 * open he can dispute that he was the driver, or the fine itself; the dispute
 * goes to his Driver Servant as a task, and nothing counts against him until the
 * outcome is recorded. Every refusal comes from the database, written for him.
 */

type MyFine = {
  fine_id: string
  notice_number: string | null
  plate: string | null
  offence_when: string
  place: string
  speeds: string
  amount_zar: number | string | null
  shift: { line: string } | null
  stage: 'awaiting_response' | 'disputed' | 'closed'
  response_due_on: string | null
  dispute_kind: 'not_the_driver' | 'the_fine' | null
  dispute_text: string | null
  disputed_on: string | null
  outcome: DisputeOutcome | null
  outcome_reasons: string | null
  reckless: boolean
  can_dispute: boolean
  consent_status: ConsentStatus | null
}

type Data = { state: string; fines: MyFine[] }

export function MyFinesCard() {
  const list = useGetMyFines<Data>()
  const dispute = useDisputeFine<Data>()
  const [openFor, setOpenFor] = useState<string | null>(null)
  const [kind, setKind] = useState<'not_the_driver' | 'the_fine' | ''>('')
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const load = list.trigger
  const refresh = useCallback(() => {
    load({}, { skipCache: true }).catch(() => undefined)
  }, [load])
  useEffect(() => {
    refresh()
  }, [refresh])

  const fines = list.data?.fines ?? []
  if (fines.length === 0) return null

  async function send(fineId: string) {
    setError(null)
    try {
      await dispute.trigger({ fine_id: fineId, kind, text })
      setDone('Your dispute is with your Driver Servant. Nothing counts against you until they record the outcome.')
      setOpenFor(null)
      setKind('')
      setText('')
      refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold">Your speeding fines</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        A speeding fine counts against you only if our shift records show you were driving and you do not dispute it, or your dispute
        is not upheld. The amount is never taken from your pay unless you sign a consent for that fine.
      </p>
      {done && <p className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">{done}</p>}
      <div className="mt-3 space-y-3">
        {fines.map((f) => (
          <div key={f.fine_id} className="rounded-lg border border-border p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{f.plate ?? 'Car'}, notice {f.notice_number ?? 'without a number'}</span>
              {f.reckless && <Chip tone="red">Above the reckless line</Chip>}
            </div>
            <p className="mt-1">{f.offence_when}</p>
            <p className="text-muted-foreground">{f.place}</p>
            <p>{f.speeds}. Amount on the notice: {rand(f.amount_zar)}</p>
            {f.shift && <p className="text-muted-foreground">Shift record: {f.shift.line}</p>}

            {f.stage === 'awaiting_response' && f.response_due_on && (
              <p className="mt-2 text-sm">You can dispute this until {readableDate(f.response_due_on)}.</p>
            )}
            {f.stage === 'disputed' && (
              <p className="mt-2 text-sm">
                You disputed this on {f.disputed_on ? readableDate(f.disputed_on) : 'a date on record'}: "{f.dispute_text}". Your Driver
                Servant will record the outcome.
              </p>
            )}
            {f.stage === 'closed' && f.outcome && (
              <p className="mt-2 text-sm">
                {/* A closed window's reasons already say it; a person's decision gets its label first. */}
                {f.outcome === 'no_dispute' ? f.outcome_reasons ?? OUTCOME_LABEL.no_dispute : `${OUTCOME_LABEL[f.outcome]}${f.outcome_reasons ? `: ${f.outcome_reasons}` : '.'}`}
              </p>
            )}
            {f.consent_status && <p className="text-muted-foreground">{CONSENT_LABEL[f.consent_status]}</p>}

            {f.can_dispute && openFor !== f.fine_id && (
              <button
                type="button"
                className="mt-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium"
                onClick={() => {
                  setOpenFor(f.fine_id)
                  setError(null)
                  setDone(null)
                }}
              >
                Dispute this fine
              </button>
            )}
            {openFor === f.fine_id && (
              <div className="mt-2 space-y-2">
                <fieldset className="space-y-1">
                  <legend className="text-sm font-medium">What is wrong?</legend>
                  <label className="flex items-center gap-2">
                    <input type="radio" name={`kind-${f.fine_id}`} checked={kind === 'not_the_driver'} onChange={() => setKind('not_the_driver')} />
                    I was not driving the car at that time
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name={`kind-${f.fine_id}`} checked={kind === 'the_fine'} onChange={() => setKind('the_fine')} />
                    The fine itself is wrong
                  </label>
                </fieldset>
                <label className="block text-sm">
                  Tell us what happened
                  <textarea
                    className="mt-1 w-full rounded-md border border-border bg-background p-2 text-sm"
                    rows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                </label>
                {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={dispute.loading}
                    className="rounded-md bg-[hsl(var(--brand))] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                    onClick={() => void send(f.fine_id)}
                  >
                    Send my dispute
                  </button>
                  <button type="button" className="px-3 py-1.5 text-sm text-muted-foreground" onClick={() => setOpenFor(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
